import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * A11yManager - Global accessibility manager component
 * 
 * This component handles accessibility concerns at the application level:
 * - Updates the document title with proper page context
 * - Manages focus when navigating between pages
 * - Announces route changes to screen readers
 * - Sets proper heading hierarchy
 */
const A11yManager: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  
  // Mapping of routes to human-readable titles
  const pageTitles: Record<string, string> = {
    '/': 'Home - HomeworkGuy',
    '/services': 'Academic Services - HomeworkGuy',
    '/how-it-works': 'How It Works - HomeworkGuy',
    '/contact': 'Contact Us - HomeworkGuy',
    '/blog': 'Academic Blog - HomeworkGuy',
    '/order': 'Place Your Order - HomeworkGuy',
    '/faq': 'Frequently Asked Questions - HomeworkGuy',
  };
  
  // Update the document title when the route changes
  useEffect(() => {
    // Extract the base route for matching
    const baseRoute = `/${location.pathname.split('/')[1]}`;
    const fullPath = location.pathname;
    
    // Set document title based on route
    const pageTitle = pageTitles[fullPath] || pageTitles[baseRoute] || 'HomeworkGuy - Professional Academic Help';
    document.title = pageTitle;
    
    // Create and use a live region to announce page changes to screen readers
    let announce = document.getElementById('page-announce');
    if (!announce) {
      announce = document.createElement('div');
      announce.id = 'page-announce';
      announce.setAttribute('aria-live', 'polite');
      announce.setAttribute('aria-atomic', 'true');
      announce.className = 'sr-only';
      document.body.appendChild(announce);
    }
    
    // Announce the page change
    announce.textContent = `Navigated to ${pageTitle}`;
    
    // Set the main landmark for screen readers
    const main = document.querySelector('main');
    if (main) {
      main.setAttribute('id', 'main-content');
      main.setAttribute('tabIndex', '-1');
    }
    
    // Handle focus management when navigating
    const focusMain = document.getElementById('main-content');
    if (focusMain) {
      // Set focus to main content area but avoid focus if using a screen reader
      // (screen readers will announce the new content automatically)
      // Need to check if a screen reader is active - this is a simplistic approach
      const isUsingScreenReader = document.body.getAttribute('data-using-screen-reader') === 'true';
      if (!isUsingScreenReader) {
        focusMain.focus({ preventScroll: true });
      }
    }
    
    // Scroll to top smoothly (if not a hash link)
    if (!location.hash) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    
    // Detect screen reader usage (basic heuristic)
    const detectScreenReader = () => {
      // If keyboard navigation or focus styles overrides are detected
      const hasCustomA11y = window.getComputedStyle(document.body).getPropertyValue('--accessibility-mode') === 'true';
      if (hasCustomA11y) {
        document.body.setAttribute('data-using-screen-reader', 'true');
      }
    };
    
    window.addEventListener('keydown', detectScreenReader, { once: true });
    
    return () => {
      window.removeEventListener('keydown', detectScreenReader);
    };
  }, [location]);
  
  return <>{children}</>;
};

export default A11yManager; 